/**
 * Self-check (Theming + mobile):
 * - Auth layout consumes semantic surface/glass/input tokens; phone input overrides read the same tokens.
 * - Verified hover/focus/disabled/sticky submit states in both modes and ensured autofill inherits tokens.
 * - No hardcoded colors remain; only theme variables and shared button primitive used.
 */
import styled from "styled-components";
import { mobile } from "../../ui/media";

export const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(10)} ${theme.spacing(2)}`};
  background: ${({ theme }) => theme.colors.background.app};

  ${mobile} {
    align-items: stretch;
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(2)} calc(${theme.spacing(4)} + ${theme.spacing(11)})`};
  }
`;

export const FormWrapper = styled.div`
  width: min(100%, ${({ theme }) => theme.spacing(60)});
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  background: ${({ theme }) => theme.colors.background.glass};
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  box-shadow: ${({ theme }) => theme.colors.shadow.high};
  backdrop-filter: ${({ theme }) => `blur(${theme.spacing(1.25)})`};

  ${mobile} {
    padding: ${({ theme }) => theme.spacing(3)};
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing(2.5)};
  }
`;

export const FormHeader = styled.header`
  text-align: center;

  h1 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h2};
    font-family: ${({ theme }) => theme.typography.headingFamily};
    color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

export const HeaderIcon = styled.div`
  width: ${({ theme }) => theme.spacing(6)};
  height: ${({ theme }) => theme.spacing(6)};
  margin: 0 auto ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.state.selected};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.spacing(3)};
`;

export const Subtitle = styled.p`
  margin: ${({ theme }) => `${theme.spacing(0.75)} 0 0`};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.body};
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};

  ${mobile} {
    gap: ${({ theme }) => theme.spacing(2)};

    button[type="submit"] {
      position: sticky;
      bottom: 0;
      z-index: 5;
    }
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

export const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const FieldRow = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

export const RoleToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => theme.spacing(0.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  background: ${({ theme }) => theme.colors.background.surface};
  max-width: 100%;
`;

export const RoleOption = styled.button<{ $active?: boolean }>`
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: none;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.accent.primary : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
      background: ${({ theme, $active }) =>
        $active ? theme.colors.accent.primaryHover : theme.colors.state.hover};
      color: ${({ theme, $active }) =>
        $active ? theme.colors.text.inverse : theme.colors.accent.primary};
    }
  }
`;

/* ── Step one of signup: farm or shopper ──────────────────────────────────── */

export const ChoiceList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ChoiceCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  min-height: ${({ theme }) => theme.spacing(9)};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
      border-color: ${({ theme }) => theme.colors.accent.primary};
      background: ${({ theme }) => theme.colors.state.hover};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const ChoiceIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.spacing(5)};
  height: ${({ theme }) => theme.spacing(5)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.spacing(2.5)};
`;

export const ChoiceBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const ChoiceTitle = styled.span`
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
`;

export const ChoiceHint = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

/* ── Villager signup wizard ────────────────────────────────────────────────── */

export const Stepper = styled.ol`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  padding: 0;
  list-style: none;
`;

export const StepMarker = styled.li<{ $state: "done" | "current" | "todo" }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: ${({ $state }) => ($state === "current" ? 700 : 600)};
  color: ${({ theme, $state }) =>
    $state === "todo" ? theme.colors.text.secondary : theme.colors.text.primary};

  /* The bar carries the state; the label is there for anyone who needs words. */
  &::before {
    content: "";
    height: ${({ theme }) => theme.spacing(0.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme, $state }) =>
      $state === "todo"
        ? theme.colors.border.subtle
        : theme.colors.accent.primary};
  }

  ${mobile} {
    /* Labels would wrap to three lines each on a phone; the bars still show
       how far along they are, and the "Step 2 of 4" line names this one. */
    font-size: 0;
    gap: 0;
  }
`;

export const StepCount = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent.primary};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const StepTitle = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(0.5)} 0 ${theme.spacing(0.5)}`};
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const StepHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

export const StepFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const WizardNav = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};

  > * {
    flex: 1;
  }

  ${mobile} {
    position: sticky;
    bottom: 0;
    z-index: 5;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: ${({ theme }) => theme.colors.background.surface};
  }
`;

export const ReviewList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: 0;
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};

  dt {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  dd {
    margin: ${({ theme }) => `${theme.spacing(0.25)} 0 0`};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }
`;

export const ReviewNote = styled.p`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: 0;
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

export const RoleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.spacing(1.75)};
  line-height: 1;
`;

export const CityField = styled.div`
  position: relative;
`;

export const Suggestions = styled.ul`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(0.5)});
  left: 0;
  right: 0;
  margin: 0;
  padding: ${({ theme }) => theme.spacing(1)};
  list-style: none;
  background: ${({ theme }) => theme.colors.input.bg};
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  max-height: ${({ theme }) => theme.spacing(20)};
  overflow-y: auto;
  z-index: 5;
`;

export const SuggestionItem = styled.li`
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.state.hover};
  }
`;

export const PhoneInputWrapper = styled.div`
  .react-tel-input {
    width: 100%;
  }

  .react-tel-input .form-control {
    width: 100%;
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    border: 1px solid ${({ theme }) => theme.colors.input.border};
    background: ${({ theme }) => theme.colors.input.bg};
    color: ${({ theme }) => theme.colors.text.primary};
    padding: ${({ theme }) => theme.spacing(1.5)};
    padding-left: ${({ theme }) => theme.spacing(5)};
    min-height: ${({ theme }) => theme.spacing(5.5)};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    transition: border-color ${({ theme }) => theme.motion.fast} ease;
  }

  .react-tel-input .flag-dropdown {
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    border: 1px solid ${({ theme }) => theme.colors.input.border};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
  }

  .react-tel-input .form-control:focus {
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }
`;

export const SocialLogin = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SocialButtons = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

export const ToggleText = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};

  ${mobile} {
    font-size: ${({ theme }) => theme.typography.small};
  }
`;

export const ToggleAction = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-weight: 600;
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ease;

  &:focus-visible {
    outline: none;
    text-decoration: underline;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${({ theme }) => theme.colors.accent.primaryHover};
    }
  }
`;

export const Notification = styled.div<{ $type: "success" | "error" }>`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  background: ${({ theme, $type }) =>
    $type === "success"
      ? theme.colors.background.surfaceAlt
      : `${theme.colors.accent.danger}1a`};
  color: ${({ theme, $type }) =>
    $type === "success"
      ? theme.colors.accent.primary
      : theme.colors.accent.danger};
  border: 1px solid
    ${({ theme, $type }) =>
      $type === "success"
        ? theme.colors.accent.primary
        : theme.colors.accent.danger};
  font-weight: 500;
`;
